import { NavLink, Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Home, MapPin, Package, Search, ScanLine, Tags, Users, Gem, Settings, Shield, LogOut, Plus, Menu, Truck } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { TrialBanner } from "@/components/TrialBanner";
import { useAuth, useWs } from "@/context/Auth";
import { useI18n } from "@/i18n/I18n";
import { cn } from "@/lib/utils";

const NAV = [
  ["/", Home, "nav.dashboard", "dashboard"], ["/locations", MapPin, "nav.locations", "locations"],
  ["/items", Package, "nav.items", "items"], ["/search", Search, "nav.search", "search"],
  ["/scan", ScanLine, "nav.scan", "scan"], ["/move", Truck, "nav.move", "move"], ["/categories", Tags, "nav.categories", "categories"],
  ["/members", Users, "nav.members", "members"], ["/plans", Gem, "nav.plans", "plans"],
  ["/settings", Settings, "nav.settings", "settings"],
];

export const Logo = ({ light }) => (
  <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
    <img src="/icons/icon-192.png" alt="" className="w-8 h-8 rounded-md" />
    <span className={cn("font-head text-xl font-extrabold tracking-tight", light ? "text-white" : "text-slate-900")}>Rangéo</span>
  </Link>
);

const NavList = ({ onNavigate, dark }) => {
  const { t } = useI18n();
  const { me, logout } = useAuth();
  const hasWs = !!me?.workspace;
  const items = [...(hasWs ? NAV : []), ...(me?.user?.is_superadmin ? [["/admin", Shield, "nav.admin", "admin"]] : [])];
  const base = dark ? "text-slate-300 hover:bg-white/5 hover:text-white" : "text-slate-700 hover:bg-slate-100";
  const active = dark ? "bg-white/10 text-white" : "bg-blue-50 text-blue-700";
  return (
    <nav className="flex flex-col gap-1">
      {items.map(([to, Icon, key, id]) => (
        <NavLink key={to} to={to} end={to === "/"} onClick={onNavigate} data-testid={`nav-${id}`}
          className={({ isActive }) => cn("flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors duration-200", isActive ? active : base)}>
          <Icon className="w-4 h-4" />{t(key)}
        </NavLink>
      ))}
      <button data-testid="nav-logout" onClick={logout} className={cn("flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors", base)}>
        <LogOut className="w-4 h-4 rtl:rotate-180" />{t("nav.logout")}
      </button>
    </nav>
  );
};

const Sidebar = () => {
  const { me } = useAuth();
  const { t } = useI18n();
  const { status, plan } = useWs();
  return (
    <aside className="hidden lg:flex w-64 shrink-0 flex-col bg-slate-900 text-white p-5 gap-8 sticky top-0 h-screen">
      <Logo light />
      {me?.workspace && (
        <div className="rounded-lg border border-white/10 p-3" data-testid="sidebar-workspace">
          <p className="text-sm font-semibold truncate">{me.workspace.name}</p>
          <p className="text-xs text-slate-400 mt-0.5">{t(`planName.${plan?.id}`)} · {t(`status.${status}`)}</p>
        </div>
      )}
      <div className="flex-1 overflow-y-auto no-scrollbar"><NavList dark /></div>
      <p className="text-xs text-slate-500 truncate">{me?.user?.email}</p>
    </aside>
  );
};

const MobileTop = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { t } = useI18n();
  return (
    <header className="lg:hidden sticky top-0 z-30 flex items-center justify-between gap-3 border-b border-slate-200/60 bg-white/80 backdrop-blur-md px-4 h-14">
      <Logo />
      <div className="flex items-center gap-1">
        <button data-testid="mobile-search-btn" onClick={() => navigate("/search")} className="p-2.5 rounded-lg hover:bg-slate-100" aria-label={t("nav.search")}><Search className="w-5 h-5" /></button>
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild><button data-testid="mobile-menu-btn" className="p-2.5 rounded-lg hover:bg-slate-100" aria-label="menu"><Menu className="w-5 h-5" /></button></SheetTrigger>
          <SheetContent side="right" className="w-72 bg-slate-900 border-slate-800 text-white">
            <SheetTitle className="sr-only">Menu</SheetTitle>
            <div className="mt-8"><NavList dark onNavigate={() => setOpen(false)} /></div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
};

const BottomBar = () => {
  const { t } = useI18n();
  const { canWrite } = useWs();
  const tab = (to, Icon, key, id) => (
    <NavLink to={to} end={to === "/"} data-testid={`bottom-${id}`}
      className={({ isActive }) => cn("flex flex-1 flex-col items-center justify-center gap-1 py-2 text-[11px] font-medium transition-colors", isActive ? "text-blue-600" : "text-slate-500")}>
      <Icon className="w-6 h-6" />{t(key)}
    </NavLink>
  );
  return (
    <nav className="lg:hidden fixed bottom-0 inset-x-0 z-30 border-t border-slate-200 bg-white/90 backdrop-blur-md pb-safe">
      <div className="flex items-stretch h-16">
        {tab("/", Home, "nav.dashboard", "dashboard")}
        {tab("/locations", MapPin, "nav.locations", "locations")}
        <div className="flex flex-1 items-start justify-center">
          {canWrite && (
            <Link to="/items/new" data-testid="bottom-add-item" aria-label={t("nav.add")}
              className="-mt-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-blue-600 text-white shadow-lg shadow-blue-600/30 active:scale-95 transition-transform">
              <Plus className="w-8 h-8" />
            </Link>
          )}
        </div>
        {tab("/scan", ScanLine, "nav.scan", "scan")}
        {tab("/items", Package, "nav.items", "items")}
      </div>
    </nav>
  );
};

export const AppShell = ({ children }) => (
  <div className="min-h-screen flex">
    <Sidebar />
    <div className="flex-1 min-w-0 flex flex-col">
      <TrialBanner />
      <MobileTop />
      <main className="flex-1 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-6 lg:py-10 pb-28 lg:pb-10">{children}</main>
    </div>
    <BottomBar />
  </div>
);

export const PageHeader = ({ title, subtitle, actions, testId = "page-title" }) => (
  <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8 fade-up">
    <div className="space-y-1">
      <h1 data-testid={testId} className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">{title}</h1>
      {subtitle && <p className="text-sm sm:text-base text-slate-500 max-w-2xl">{subtitle}</p>}
    </div>
    {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
  </div>
);
